import React, { useEffect } from 'react';
import { useForm, Controller, useWatch, FormProvider } from 'react-hook-form';
import Helmet from 'react-helmet';
import PageHeader from 'retired/PageHeader';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectUserLocations, selectUser } from 'entities/user/selectors';
import { Features } from 'utils/features';
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  LinearProgress,
  Link,
  Switch,
  Typography,
} from '@mui/material';
import { ConfidenceScoreOperator as ConfidenceScoreOperatorEnum } from 'generated/graphql';

import Box from 'retired/Box';
import moment, { Moment } from 'moment';

import { AIConfidenceScoreFields } from './AIConfidenceScoreFields';
import { DonationAmountFields } from './DonationAmountFields';
import { DateRangeFields } from './DateRangeFields';
import { RuleTypeField } from './RuleTypeField';
import {
  useSetExternalMatchRequestRulesMutation,
  useGetExternalMatchRequestRulesQuery,
  ExternalMatchRequestRuleType,
} from 'generated/graphql';
import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import { convertRulesToInput } from '../helpers';
import { useRulesContext } from '../RulesContext';
import FormLocationSelector from 'components/ReactHookForm/controlledLocationSelector/FormLocationSelector';
import { getUserFriendlyErrorMessage } from '../getUserFriendlyErrorMessage';

export type FormValues = {
  ruleType: ExternalMatchRequestRuleType;
  donationAmount: string;
  minAmount: number;
  maxAmount: number;
  employeeLocations: string[];
  active: boolean;
  confidenceScoreOperator: ConfidenceScoreOperatorEnum;
  confidenceScoreValue: string;
  startingAt: Moment | null;
  endingAt: Moment | null;
};

const ExternalMatchRequestRules: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { rules: contextRules, setRules } = useRulesContext();
  const locations = useSelector(selectUserLocations);
  const user = useSelector(selectUser);

  const { id } = useParams<{ id: string }>();

  const hasAIConfidenceScore = user?.hasFeature(Features.AI_RECEIPT_READER) || false;

  // Fetch rules if not in context
  const { data: existingRulesData, loading: loadingRules } = useGetExternalMatchRequestRulesQuery({
    skip: contextRules.length > 0, // Skip if we have rules from context
    fetchPolicy: 'network-only',
  });

  // Update context when rules are fetched
  useEffect(() => {
    if (existingRulesData?.externalMatchRequestRules && contextRules.length === 0) {
      setRules(existingRulesData.externalMatchRequestRules);
    }
  }, [existingRulesData, contextRules.length, setRules]);

  const [setExternalMatchRequestRulesMutation, { loading: submitting }] =
    useSetExternalMatchRequestRulesMutation({
      onCompleted: () => {
        dispatch(showMessageAction(id ? 'Rule updated successfully' : 'Rule created successfully'));
        history.push('/donations/external-match-request-rules');
      },
      onError: (error) => {
        const userFriendlyMessage = getUserFriendlyErrorMessage(error);
        dispatch(showErrorAction(userFriendlyMessage));
      },
    });

  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      ruleType: ExternalMatchRequestRuleType.Approval,
      donationAmount: '',
      minAmount: 0,
      maxAmount: 1000,
      employeeLocations: [],
      active: true,
      confidenceScoreOperator: ConfidenceScoreOperatorEnum.MoreOrEqual,
      confidenceScoreValue: '',
      startingAt: null,
      endingAt: null,
    },
  });

  const { handleSubmit, control, trigger, reset } = methods;

  // Watch fields for cross-validation
  const startDate = useWatch({ control, name: 'startingAt' });
  const endDate = useWatch({ control, name: 'endingAt' });
  const minAmount = useWatch({ control, name: 'minAmount' });
  const maxAmount = useWatch({ control, name: 'maxAmount' });

  // Trigger validation when watched fields change
  useEffect(() => {
    trigger(['startingAt', 'endingAt']);
  }, [startDate, endDate, trigger]);

  useEffect(() => {
    trigger(['minAmount', 'maxAmount']);
  }, [minAmount, maxAmount, trigger]);

  // Populate form with existing rule data when editing
  useEffect(() => {
    if (id && contextRules.length > 0) {
      const ruleToEdit = contextRules.find((rule: any) => rule.id === id);

      if (ruleToEdit) {
        const formData: FormValues = {
          ruleType: ruleToEdit.type,
          donationAmount: '',
          minAmount: ruleToEdit.minAmount,
          maxAmount: ruleToEdit.maxAmount,
          employeeLocations: ruleToEdit.employeeLocations || [],
          active: ruleToEdit.active,
          confidenceScoreOperator:
            ruleToEdit.confidenceScore?.operator || ConfidenceScoreOperatorEnum.MoreOrEqual,
          confidenceScoreValue: ruleToEdit.confidenceScore?.value?.toString() || '',
          startingAt: ruleToEdit.startingAt ? moment(ruleToEdit.startingAt) : null,
          endingAt: ruleToEdit.endingAt ? moment(ruleToEdit.endingAt) : null,
        };

        reset(formData);
      }
    }
  }, [id, contextRules, reset]);

  const onSubmit = async (data: FormValues) => {
    try {
      // Transform form data to match GraphQL mutation input format
      const newRuleInput = {
        id,
        type: data.ruleType,
        active: data.active,
        minAmount: parseInt(data.minAmount.toString(), 10),
        maxAmount: parseInt(data.maxAmount.toString(), 10),
        confidenceScore:
          data.confidenceScoreOperator && data.confidenceScoreValue
            ? {
                operator: data.confidenceScoreOperator,
                value: parseFloat(data.confidenceScoreValue),
              }
            : undefined,
        employeeLocations: data.employeeLocations,
        startingAt: data.startingAt ? data.startingAt.toISOString() : undefined,
        endingAt: data.endingAt ? data.endingAt.toISOString() : undefined,
      };

      // Get existing rules from context and convert to input format
      const existingRulesInput = convertRulesToInput(contextRules);

      let updatedRules;

      if (id) {
        // Update existing rule - find the rule by ID and replace it
        const ruleIndex = contextRules.findIndex((rule) => rule.id === id);

        if (ruleIndex === -1) {
          throw new Error('Rule not found');
        }

        updatedRules = [...existingRulesInput];
        updatedRules[ruleIndex] = newRuleInput;
      } else {
        // Add new rule to the end of the array
        updatedRules = [...existingRulesInput, newRuleInput];
      }

      await setExternalMatchRequestRulesMutation({
        variables: {
          rules: updatedRules,
        },
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      // Error notification is handled by the mutation's onError callback
      // This catch block prevents unhandled promise rejections
    }
  };

  return (
    <>
      <Helmet title={`${id ? 'Edit' : 'New'} External Donation Auto Response Rule`} />

      <PageHeader
        title={`${id ? 'Edit a' : 'Create a new'} rule`}
        onArrowClick={() => history.push('/donations/external-match-request-rules')}
      />

      {loadingRules && <LinearProgress />}

      {!loadingRules && (
        <Box>
          {submitting ? (
            <CircularProgress />
          ) : (
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '48px',
                  }}
                >
                  <RuleTypeField control={control} />

                  <FormControl>
                    <div
                      style={{
                        flexDirection: 'column',
                        display: 'flex',
                        gap: '10px',
                      }}
                    >
                      <FormLabel sx={{ marginTop: '10px' }}>Status</FormLabel>
                      <Controller
                        name="active"
                        control={control}
                        render={({ field }) => (
                          <div>
                            <Switch
                              checked={field.value}
                              onChange={field.onChange}
                              color="primary"
                            />
                            <FormLabel sx={{ marginTop: '10px' }}>
                              {field.value ? 'Active' : 'Inactive'}
                            </FormLabel>
                          </div>
                        )}
                      />
                    </div>
                  </FormControl>

                  <DonationAmountFields
                    control={control}
                    minAmount={minAmount}
                    maxAmount={maxAmount}
                  />

                  <div></div>

                  <AIConfidenceScoreFields
                    control={control}
                    hasAIConfidenceScore={hasAIConfidenceScore}
                  />

                  {!hasAIConfidenceScore ? (
                    <Alert
                      severity="info"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        alignSelf: 'end',
                        minHeight: '52px',
                        width: 'fit-content',
                      }}
                    >
                      <Typography fontWeight="bold" mb="8px">
                        Boost your review process with MatchAI!
                      </Typography>
                      <Typography mb="8px">
                        Reach out to your Partner Success Manager to turn on AI Confidence Scores
                        for your organization.
                      </Typography>
                      <Typography>
                        <Link
                          href="https://resources.joindeed.com/deed-resources/deeds-match-ai"
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="always"
                        >
                          Discover how MatchAI works
                        </Link>
                      </Typography>
                    </Alert>
                  ) : (
                    <div />
                  )}

                  <FormLocationSelector
                    control={control}
                    name="employeeLocations"
                    locations={locations?.toArray?.() || []}
                    label="Employee Location (optional)"
                    placeholder="Select employee locations..."
                    helperText="Select locations where this rule should apply"
                  />

                  <DateRangeFields control={control} startDate={startDate} endDate={endDate} />

                  <div style={{ gridColumn: '1 / -1' }}>
                    <Typography align="right">
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={submitting}
                        size="large"
                      >
                        Submit
                      </Button>
                    </Typography>
                  </div>
                </div>
              </form>
            </FormProvider>
          )}
        </Box>
      )}
    </>
  );
};

export default ExternalMatchRequestRules;
